import type {
  ExpectedReceipt,
  ExpectedReceiptInput,
  PostReceiptResult,
  ReceiptCount,
} from '../domain/goods-intake';
import { expectedReceiptSchema } from '../domain/validation';
import { ApiError } from './errors';
import type { GoodsIntakeApi, ReceiptFilters } from './goods-intake-api';
import { fulfillmentErrorFrom } from './live-fulfillment-api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

function parseReceipts(raw: unknown): ExpectedReceipt[] {
  return Array.isArray(raw) ? raw.map((row) => expectedReceiptSchema.parse(row)) : [];
}

/** Talks to leatherback's StockService through the local relay (see server/). */
export class LiveGoodsIntakeApi implements GoodsIntakeApi {
  private async fetchJSON(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      throw fulfillmentErrorFrom(response.status, body);
    }
    return body;
  }

  private post(path: string, body: unknown): Promise<Record<string, unknown>> {
    return this.fetchJSON(path, { method: 'POST', body: JSON.stringify(body) });
  }

  private receiptPath(id: string, action = ''): string {
    return `/v1/expectedReceipts/${encodeURIComponent(id)}${action ? `:${action}` : ''}`;
  }

  async listReceipts(filters?: ReceiptFilters): Promise<ExpectedReceipt[]> {
    const rows: ExpectedReceipt[] = [];
    const params = new URLSearchParams({ pageSize: '1000' });
    if (filters?.siteId) params.set('siteId', filters.siteId);
    if (filters?.openOnly) params.set('openOnly', 'true');
    for (;;) {
      const raw = await this.fetchJSON(`/v1/expectedReceipts?${params.toString()}`);
      rows.push(...parseReceipts(raw.expectedReceipts));
      const next = raw.nextPageToken;
      if (typeof next !== 'string' || !next) return rows;
      params.set('pageToken', next);
    }
  }

  async getReceipt(id: string): Promise<ExpectedReceipt> {
    return expectedReceiptSchema.parse(await this.fetchJSON(this.receiptPath(id)));
  }

  async findReceipts(code: string): Promise<ExpectedReceipt[]> {
    const params = new URLSearchParams({ code });
    const raw = await this.fetchJSON(`/v1/expectedReceipts:find?${params.toString()}`);
    return parseReceipts(raw.expectedReceipts);
  }

  async createReceipt(input: ExpectedReceiptInput): Promise<ExpectedReceipt> {
    const raw = await this.post('/v1/expectedReceipts', {
      siteId: input.siteId,
      supplier: input.supplier?.trim() || undefined,
      supplierReference: input.supplierReference?.trim() || undefined,
      identifiers: input.identifiers.map((id) => id.trim()).filter(Boolean),
      source: 'MANUAL',
      expectedAt: input.expectedAt || undefined,
      notes: input.notes?.trim() || undefined,
      lines: input.lines.map((line) => ({
        stockItemId: line.stockItemId,
        expectedQuantity: String(line.expectedQuantity),
      })),
    });
    return expectedReceiptSchema.parse(raw);
  }

  async recordIntake(
    id: string,
    version: number,
    counts: ReceiptCount[],
  ): Promise<ExpectedReceipt> {
    const raw = await this.post(this.receiptPath(id, 'recordIntake'), {
      version: String(version),
      counts: counts.map((count) => ({
        id: count.lineId || undefined,
        stockItemId: count.lineId ? undefined : count.stockItemId,
        receivedQuantity: String(count.receivedQuantity),
        damagedQuantity: String(count.damagedQuantity),
        locationId: count.locationId || undefined,
        note: count.note?.trim() ?? undefined,
      })),
    });
    return expectedReceiptSchema.parse(raw);
  }

  async postReceipt(id: string, version: number): Promise<PostReceiptResult> {
    const raw = await this.post(this.receiptPath(id, 'post'), { version: String(version) });
    return {
      receipt: expectedReceiptSchema.parse(raw.expectedReceipt),
      replayed: raw.replayed === true,
    };
  }

  async cancelReceipt(id: string, version: number, reason: string): Promise<ExpectedReceipt> {
    const raw = await this.post(this.receiptPath(id, 'cancel'), {
      version: String(version),
      reason: reason.trim(),
    });
    return expectedReceiptSchema.parse(raw);
  }
}

export const goodsIntakeApi = new LiveGoodsIntakeApi();
